import { useEffect, useRef, useState } from 'react';
import { availableLanguages, currentLang, LANGUAGES, setLang, t } from '../i18n/index.ts';
import { Icon } from './Icon.tsx';

/**
 * The language of this device: a small chip showing the current one, opening the list with
 * each language under its own name. Choosing one shows the page again in that language. With
 * only one language turned on for the business, there is nothing to choose and it stays hidden.
 */
export function LanguageSwitch({ align = 'right' }: { align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const lang = currentLang();
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  const current = LANGUAGES.find((l) => l.code === lang)!;
  const offered = availableLanguages();
  if (offered.length < 2) return null;
  return (
    <div className="place-switch lang-switch" ref={box}>
      <button
        type="button"
        className="place-chip lang-chip"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('lang.label', { name: current.name })}
        title={t('lang.label', { name: current.name })}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="globe" size={16} />
        <span className="lang-chip-code">{lang.toUpperCase()}</span>
      </button>
      {open ? (
        <div className={`topnav-sheet place-sheet lang-sheet${align === 'left' ? ' lang-sheet-left' : ''}`} role="menu" aria-label={t('lang.choose')}>
          <p className="place-sheet-title">{t('lang.choose')}</p>
          {offered.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={l.code === lang}
              lang={l.code}
              className={`topnav-sheet-link${l.code === lang ? ' active' : ''}`}
              onClick={() => {
                setOpen(false);
                setLang(l.code);
              }}
            >
              <Icon name={l.code === lang ? 'checkCircle' : 'globe'} size={20} />
              <span className="place-sheet-name">{l.name}</span>
              <span className="place-chip-code">{l.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
